import React from "react";

const PartnerDetailsSkeleton = () => {
	return (
		<div className='p-6 animate-pulse'>
			{/* Back Button Skeleton */}
			<div className='mb-4'>
				<div className='h-5 w-40 bg-gray-200 rounded'></div>
			</div>

			{/* Header Skeleton */}
			<div className='flex items-start justify-between mb-6 flex-wrap gap-4'>
				<div className='flex-1'>
					<div className='flex items-center gap-3 mb-2'>
						<div className='h-8 w-64 bg-gray-200 rounded'></div>
						<div className='h-6 w-20 bg-gray-200 rounded-full'></div>
					</div>
					<div className='h-4 w-80 bg-gray-200 rounded'></div>
				</div>
				<div className='flex gap-3 flex-wrap'>
					<div className='h-10 w-24 bg-gray-200 rounded-lg'></div>
					<div className='h-10 w-32 bg-gray-200 rounded-lg'></div>
					<div className='h-10 w-28 bg-gray-200 rounded-lg'></div>
				</div>
			</div>

			{/* Main Content Grid */}
			<div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
				{/* Left Column - Business Details */}
				<div className='lg:col-span-2 space-y-6'>
					{/* Business Details Card Skeleton */}
					<div className='bg-white rounded-xl p-6 shadow-sm'>
						<div className='flex items-center gap-2 mb-6'>
							<div className='h-6 w-6 bg-gray-200 rounded'></div>
							<div className='h-6 w-40 bg-gray-200 rounded'></div>
						</div>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
							{Array.from({ length: 10 }).map((_, index) => (
								<div key={index}>
									<div className='h-4 w-32 bg-gray-200 rounded mb-2'></div>
									<div className='h-10 w-full bg-gray-100 rounded-lg'></div>
								</div>
							))}
						</div>
					</div>

					{/* Gallery View Skeleton */}
					<div className='bg-white rounded-xl p-6 shadow-sm'>
						<div className='flex items-center justify-between mb-4'>
							<div className='flex items-center gap-2'>
								<div className='h-5 w-5 bg-gray-200 rounded'></div>
								<div className='h-6 w-32 bg-gray-200 rounded'></div>
							</div>
							<div className='h-10 w-28 bg-gray-200 rounded-lg'></div>
						</div>
						<div className='h-4 w-64 bg-gray-200 rounded mb-4'></div>
						<div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
							{Array.from({ length: 6 }).map((_, index) => (
								<div
									key={index}
									className='aspect-video bg-gray-200 rounded-lg'
								></div>
							))}
						</div>
					</div>

					{/* Business Bio Skeleton */}
					<div className='bg-white rounded-xl p-6 shadow-sm'>
						<div className='h-6 w-32 bg-gray-200 rounded mb-4'></div>
						<div className='space-y-2'>
							<div className='h-4 w-full bg-gray-200 rounded'></div>
							<div className='h-4 w-full bg-gray-200 rounded'></div>
							<div className='h-4 w-3/4 bg-gray-200 rounded'></div>
						</div>
					</div>
				</div>

				{/* Right Column - Admin Management & Stats */}
				<div className='space-y-6'>
					{/* Admin Management Skeleton */}
					<div className='bg-white rounded-xl p-6 shadow-sm'>
						<div className='h-6 w-40 bg-gray-200 rounded mb-6'></div>
						<div className='space-y-5'>
							<div className='flex items-center justify-between'>
								<div className='h-4 w-24 bg-gray-200 rounded'></div>
								<div className='h-6 w-16 bg-gray-200 rounded-full'></div>
							</div>
							{Array.from({ length: 4 }).map((_, index) => (
								<div key={index}>
									<div className='h-4 w-28 bg-gray-200 rounded mb-2'></div>
									<div className='h-4 w-40 bg-gray-200 rounded'></div>
								</div>
							))}
							<div>
								<div className='h-4 w-24 bg-gray-200 rounded mb-2'></div>
								<div className='h-20 w-full bg-gray-100 rounded-lg'></div>
							</div>
						</div>
					</div>

					{/* Business Logo Skeleton */}
					<div className='bg-white rounded-xl p-6 shadow-sm'>
						<div className='h-6 w-32 bg-gray-200 rounded mb-4'></div>
						<div className='w-full h-48 bg-gray-200 rounded-lg'></div>
					</div>

					{/* Stats Cards Skeleton */}
					{Array.from({ length: 2 }).map((_, index) => (
						<div key={index} className='bg-white rounded-xl p-6 shadow-sm'>
							<div className='flex items-center gap-3 mb-2'>
								<div className='h-6 w-6 bg-gray-200 rounded'></div>
								<div className='h-4 w-32 bg-gray-200 rounded'></div>
							</div>
							<div className='h-8 w-24 bg-gray-200 rounded'></div>
						</div>
					))}
				</div>
			</div>

			{/* Bookings and Wallet Section Skeleton */}
			<div className='mt-12 space-y-8'>
				{/* Bookings Tabs Skeleton */}
				<div className='bg-white rounded-xl p-6 shadow-sm'>
					<div className='h-6 w-40 bg-gray-200 rounded mb-4'></div>
					<div className='space-y-4'>
						{/* Table Header */}
						<div className='grid grid-cols-5 gap-4 pb-4 border-b border-gray-200'>
							{Array.from({ length: 5 }).map((_, index) => (
								<div key={index} className='h-4 bg-gray-200 rounded'></div>
							))}
						</div>
						{/* Table Rows */}
						{Array.from({ length: 5 }).map((_, rowIndex) => (
							<div
								key={rowIndex}
								className='grid grid-cols-5 gap-4 py-4 border-b border-gray-100'
							>
								{Array.from({ length: 5 }).map((_, colIndex) => (
									<div key={colIndex} className='h-4 bg-gray-200 rounded'></div>
								))}
							</div>
						))}
					</div>
				</div>

				{/* Wallet Table Skeleton */}
				<div className='bg-white rounded-xl p-6 shadow-sm'>
					<div className='h-6 w-48 bg-gray-200 rounded mb-4'></div>
					<div className='space-y-4'>
						{/* Table Header */}
						<div className='grid grid-cols-4 gap-4 pb-4 border-b border-gray-200'>
							{Array.from({ length: 4 }).map((_, index) => (
								<div key={index} className='h-4 bg-gray-200 rounded'></div>
							))}
						</div>
						{/* Table Rows */}
						{Array.from({ length: 5 }).map((_, rowIndex) => (
							<div
								key={rowIndex}
								className='grid grid-cols-4 gap-4 py-4 border-b border-gray-100'
							>
								{Array.from({ length: 4 }).map((_, colIndex) => (
									<div key={colIndex} className='h-4 bg-gray-200 rounded'></div>
								))}
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default PartnerDetailsSkeleton;
